/**
 * NOTE: This component is largely a copy of `Login.screen.tsx`, with the main difference being the styling
 * on the form wrappers and the removal of any redirects during authentication.
 *
 * This is used to support login with simple sign on, and should eventually be abstracted out in such a way that
 * it can be re-used both here and in the original login screen.
 */

import * as React from 'react';
import Router, { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import styles from 'legacy/common/components/form/Form.module.scss';
import Button from 'legacy/common/components/button/Button';

import {
  useConfirmLoginVerificationSessionCommand,
  useCreateLoginVerificationSessionCommand,
  useUpdateLoginVerificationSessionCommand
} from 'legacy/auth/hooks';
import { handleLoginNoRedirect } from 'legacy/auth/helpers';
import { LoginResponse, VerificationSessionDto } from 'legacy/auth/types';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import { LoginConfirmationModalContent } from '../login/LoginConfirmation';
import LoginWithPasswordForm from 'legacy/auth/components/login/LoginWithPasswordForm';
import FormWrapper from '../FormWrapper';
import LoginForm from '../login/LoginForm';
import LoginMethodsList from '../login/LoginMethodsList';
import { ErrorResponse } from 'legacy/common/utils/error';

const handleLegacyEmailLogin = (email: string, from: string) => {
  Router.push(
    {
      pathname: '/login',
      query: {
        type: 'email',
        email,
        from
      }
    },
    '/login'
  );
};

const handleLegacyPhoneLogin = (phone: string, from: string) => {
  Router.push(
    {
      pathname: '/login',
      query: {
        type: 'phone',
        phone,
        from
      }
    },
    '/login'
  );
};

const SignUpButton = ({ onClick }) => {
  const { t } = useTranslation('auth');

  return (
    <>
      <div className={styles.separator}>
        <span />
        <div>{t('common.or')}</div>
        <span />
      </div>
      <div className="mt-6">
        <Button onClick={onClick} data-test="sign-up">
          {t('buttons.signUp')}
        </Button>
      </div>
    </>
  );
};

type LoginAuthenticationFlowProps = {
  onCompleteLogin: (loginResponse: LoginResponse) => void;
  onFailedLogin?: (error: ErrorResponse) => void;
  onClickSignUp: () => void;
};
const LoginAuthenticationFlow = ({
  onCompleteLogin,
  onFailedLogin,
  onClickSignUp
}: LoginAuthenticationFlowProps) => {
  const router = useRouter();
  const flags = useFlags();

  const [identifier, setIdentifier] = React.useState('');
  const [showMoreOptions, setShowMoreOptions] = React.useState(false);
  const [showMethodsList, setShowMethodsList] = React.useState(false);
  const [confirmationError, setConfirmationError] = React.useState<
    string | undefined
  >();

  const [verificationSession, setVerificationSession] =
    React.useState<VerificationSessionDto>();
  const createLoginVerificationSessionCommand =
    useCreateLoginVerificationSessionCommand();
  const updateLoginVerificationSessionCommand =
    useUpdateLoginVerificationSessionCommand();
  const confirmLoginVerificationSessionCommand =
    useConfirmLoginVerificationSessionCommand();

  const handleInitialLogin = (values: {
    email?: string;
    phoneNumber?: string;
    callingCode: string;
    countryCode: string;
  }) => {
    const emailValue = values.email;
    const phoneValue = values.phoneNumber;

    // Use the current view (showMoreOptions) to determine which method to use
    // showMoreOptions: false = email view, true = phone view
    if (!showMoreOptions && emailValue) {
      setIdentifier(emailValue);

      // Determine if OTP should be required based on the feature flag
      const isOtpEnabled = flags[FLAGS.ENABLE_OTP_EMAIL_LOGIN];

      createLoginVerificationSessionCommand.mutate(
        {
          email: emailValue,
          // Only set requireOtp for email logins, based on the feature flag
          requireOtp: isOtpEnabled
        },
        {
          onSuccess: (data) => {
            const session = data as VerificationSessionDto;

            if ('isLegacyAuthUser' in data && data.isLegacyAuthUser) {
              handleLegacyEmailLogin(emailValue, router.asPath);
              return;
            }

            setVerificationSession({
              ...session,
              deliveryMethod: 'email'
            });
          }
        }
      );
      return;
    }

    if (showMoreOptions && phoneValue) {
      setIdentifier(phoneValue);

      createLoginVerificationSessionCommand.mutate(
        {
          phoneNumber: phoneValue,
          countryCode: values.callingCode
        },
        {
          onSuccess: (data) => {
            if ('isLegacyAuthUser' in data && data.isLegacyAuthUser) {
              handleLegacyPhoneLogin(phoneValue, router.asPath);
              return;
            }

            setVerificationSession({
              ...(data as VerificationSessionDto),
              deliveryMethod: 'sms'
            });
          }
        }
      );
    }
  };

  const handlePasswordLogin = (values: { password: string }) => {
    if (!verificationSession?.verificationSessionId) {
      // This should technically never happen, but just in case
      return;
    }

    // Clear any previous confirmation errors when submitting password
    setConfirmationError(undefined);

    updateLoginVerificationSessionCommand.mutate(
      {
        verificationSessionId: verificationSession.verificationSessionId,
        password: values.password
      },
      {
        onSuccess: (data) => {
          const isOtpEnabled = flags[FLAGS.ENABLE_OTP_EMAIL_LOGIN];

          // If OTP is disabled for EMAIL logins only, skip OTP step and log in directly
          // Phone logins always require OTP verification regardless of this flag
          if (!isOtpEnabled && verificationSession.deliveryMethod === 'email') {
            // Clear any previous confirmation errors
            setConfirmationError(undefined);

            confirmLoginVerificationSessionCommand.mutate(
              {
                verificationSessionId: verificationSession.verificationSessionId
              },
              {
                onSuccess: (loginData) => {
                  handleLoginNoRedirect(loginData);
                  onCompleteLogin(loginData);
                },
                onError: (err) => {
                  // Store the error so it can be displayed in the password form
                  setConfirmationError(err.message);
                  onFailedLogin?.(err);
                }
              }
            );
            return;
          }

          // For phone logins or when email OTP is enabled, show OTP verification screen
          setVerificationSession({
            ...data,
            deliveryMethod: verificationSession.deliveryMethod
          });
        }
      }
    );
  };

  const onReset = () => {
    setVerificationSession(null);
    setShowMethodsList(false);
    setConfirmationError(undefined);

    createLoginVerificationSessionCommand.reset();
    updateLoginVerificationSessionCommand.reset();
    confirmLoginVerificationSessionCommand.reset();
  };

  const handleSelectMethod = (method: 'email' | 'phone') => {
    setShowMoreOptions(method === 'phone');
    setShowMethodsList(false);
  };

  const handleConfirmLogin = (code: string) => {
    confirmLoginVerificationSessionCommand.mutate(
      {
        smsCode: code,
        verificationSessionId: verificationSession.verificationSessionId
      },
      {
        onSuccess: (data) => {
          handleLoginNoRedirect(data);
          onCompleteLogin(data);
        },
        onError: (err) => onFailedLogin?.(err)
      }
    );
  };

  if (
    (verificationSession &&
      !verificationSession.requiresPasswordVerification) ||
    verificationSession?.hasVerifiedPassword
  ) {
    return (
      <LoginConfirmationModalContent
        onBackClick={onReset}
        verificationSession={verificationSession}
        onSubmit={handleConfirmLogin}
        isLoading={confirmLoginVerificationSessionCommand.isLoading}
        errorMessage={confirmLoginVerificationSessionCommand.error?.message}
      />
    );
  }

  if (verificationSession?.requiresPasswordVerification) {
    return (
      <FormWrapper isModal>
        <LoginWithPasswordForm
          phoneOrEmail={identifier}
          onReset={onReset}
          onSubmit={handlePasswordLogin}
          isLoading={
            updateLoginVerificationSessionCommand.isLoading ||
            confirmLoginVerificationSessionCommand.isLoading
          }
          errorMessage={
            updateLoginVerificationSessionCommand.error?.message ||
            confirmationError
          }
        />
        <SignUpButton onClick={onClickSignUp} />
      </FormWrapper>
    );
  }

  if (showMethodsList) {
    return (
      <LoginMethodsList
        currentMethod={showMoreOptions ? 'phone' : 'email'}
        onSelectMethod={handleSelectMethod}
        onBack={() => setShowMethodsList(false)}
        isModal
      />
    );
  }

  return (
    <FormWrapper isModal>
      <LoginForm
        onSubmit={handleInitialLogin}
        isLoading={createLoginVerificationSessionCommand.isLoading}
        errorMessage={createLoginVerificationSessionCommand.error?.message}
        showMoreOptions={showMoreOptions}
        onShowMethodsList={() => setShowMethodsList(true)}
      />
      <SignUpButton onClick={onClickSignUp} />
    </FormWrapper>
  );
};

export default LoginAuthenticationFlow;
